import { useEffect, useRef, useState, useCallback } from "react";
import { HubConnectionBuilder, HubConnectionState, HttpTransportType, LogLevel, type HubConnection } from "@microsoft/signalr";
import { getEnv } from "@trillion/ui/lib/env";
import type { CollaborationStatus, CollaboratorCursor, DiagramConflict, DiagramJoin, DiagramRefresh, DiagramSaveResult,
  DiagramSyncCheckpoint, DiagramUpdate, PresenceClient } from "../types/board";
import { compareRevision } from "./diagram-collaboration";
import { cursorPositionSchema } from "../validation/cursor-position";

export type { PresenceClient } from "../types/board";
interface SyncOptions {
  onBaseline?: (baseline: DiagramJoin) => Promise<boolean>;
  onCheckpoint?: (checkpoint: DiagramSyncCheckpoint) => void;
  onRefresh?: (refresh: DiagramRefresh) => Promise<boolean>;
  onRefreshError?: () => Promise<void>;
  onUnavailable?: () => void;
}
export interface DiagramSyncState {
  snapshot: DiagramSyncCheckpoint | null; presence: PresenceClient[]; cursors: CollaboratorCursor[];
  connectionId: string | null; status: CollaborationStatus; isConnected: boolean;
  retry: () => void; refresh: () => void; degrade: () => void; retire: () => void;
  sendUpdate: (payload: DiagramUpdate) => Promise<DiagramSaveResult | DiagramConflict>;
  sendCursor: (x: number, y: number) => void;
}

const retries = [0, 2_000, 10_000, 30_000];

/** Transport lifetime only; the page reconciles authority before a joined connection can send. */
export function useDiagramSync(diagramId: string | undefined, options: SyncOptions = {}): DiagramSyncState {
  const optionsRef = useRef(options); optionsRef.current = options;
  const connectionRef = useRef<HubConnection | null>(null);
  const controls = useRef({ retry: () => {}, refresh: () => {}, degrade: () => {}, retire: () => {}, cursor: (_x: number, _y: number) => {} });
  const ready = useRef(false);
  const [snapshot, setSnapshot] = useState<DiagramSyncCheckpoint | null>(null);
  const [presence, setPresence] = useState<PresenceClient[]>([]);
  const [cursors, setCursors] = useState<CollaboratorCursor[]>([]);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [status, setStatus] = useState<CollaborationStatus>("connecting");

  useEffect(() => {
    let active = true, terminal = false, epoch = 0, retryCount = 0, joining = false;
    let lastRefresh = 0, observedRevision: string | null = null;
    let roster = new Set<string>();
    let refreshFlight: object | null = null, refreshAgain = false, needsJoin = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let pendingCursor: { x: number; y: number } | null = null, cursorFlight = false;
    const timeoutTimers = new Set<ReturnType<typeof setTimeout>>();
    const alive = () => active && !terminal;
    const transition = (next: CollaborationStatus) => { if (alive()) { ready.current = next === "connected"; setStatus(next); } };
    setSnapshot(null); setPresence([]); setCursors([]); setConnectionId(null); ready.current = false;
    if (!diagramId) { setStatus("disconnected"); return; }
    transition("connecting");
    const connection = new HubConnectionBuilder()
      .withUrl(`${getEnv("BOARD_API_URL")}/hubs/diagram`, {
        withCredentials: true, transport: HttpTransportType.WebSockets, skipNegotiation: true,
      })
      .withAutomaticReconnect(retries)
      .configureLogging(LogLevel.Warning).build();
    connectionRef.current = connection;

    async function bounded<T>(promise: Promise<T>): Promise<T> {
      let timer: ReturnType<typeof setTimeout>;
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error("Collaboration refresh timed out")), 5_000);
        timeoutTimers.add(timer);
      });
      try { return await Promise.race([promise, timeout]); }
      finally { clearTimeout(timer!); timeoutTimers.delete(timer!); }
    }
    function installRoster(clients: PresenceClient[]) {
      if (!Array.isArray(clients) || clients.some(client => !client || typeof client.connectionId !== "string" || typeof client.userId !== "string"))
        throw new Error("Invalid collaboration roster");
      roster = new Set(clients.map(client => client.connectionId));
      setPresence(clients);
      setCursors(current => current.filter(cursor => roster.has(cursor.connectionId)));
    }
    function retire() {
      if (!alive()) return;
      terminal = true; epoch++; ready.current = false; pendingCursor = null;
      clearTimeout(retryTimer); setStatus("disconnected"); setPresence([]); setCursors([]);
      void connection.stop();
    }
    async function room(join: boolean) {
      if (!alive() || connection.state !== HubConnectionState.Connected) return;
      if (refreshFlight) { refreshAgain = true; return; }
      const flight = {}; refreshFlight = flight; const currentEpoch = epoch;
      const current = () => alive() && currentEpoch === epoch;
      try {
        if (join) {
          transition(lastRefresh ? "reconnecting" : "connecting");
          const baseline = await bounded(connection.invoke<DiagramJoin>("JoinRoom", diagramId));
          if (!current()) return;
          if (baseline?.protocol_version !== 1 || baseline.diagram?.diagram_id !== diagramId || !baseline.connection_id)
            throw new Error("Collaboration protocol unavailable");
          compareRevision(baseline.diagram.revision, baseline.diagram.revision);
          const reconciled = await bounded(optionsRef.current.onBaseline?.(baseline) ?? Promise.resolve(true));
          if (!current()) return;
          installRoster(baseline.presence); setConnectionId(baseline.connection_id);
          observedRevision = baseline.diagram.revision; needsJoin = false; lastRefresh = Date.now(); retryCount = 0;
          transition(reconciled && baseline.sync_status === "connected" ? "connected" : "degraded");
        } else {
          const result = await bounded(connection.invoke<DiagramRefresh>("RefreshRoom", diagramId));
          if (!current()) return;
          if (result?.protocol_version !== 1 || result.diagram_id !== diagramId) throw new Error("Collaboration protocol unavailable");
          if (observedRevision && compareRevision(result.revision, observedRevision) > 0) transition("degraded");
          const reconciled = await bounded(optionsRef.current.onRefresh?.(result) ?? Promise.resolve(true));
          if (!current()) return;
          installRoster(result.presence); observedRevision = result.revision; lastRefresh = Date.now();
          transition(reconciled && result.sync_status === "connected" ? "connected" : "degraded");
        }
      } catch {
        if (current()) {
          needsJoin = true; transition("degraded"); pendingCursor = null;
          await optionsRef.current.onRefreshError?.().catch(() => {});
        }
      } finally {
        if (refreshFlight === flight) {
          refreshFlight = null;
          if (refreshAgain && current()) { refreshAgain = false; void room(needsJoin); }
        }
      }
    }
    async function start() {
      if (!alive() || joining) return;
      clearTimeout(retryTimer);
      if (connection.state === HubConnectionState.Connected) { void room(true); return; }
      if (connection.state !== HubConnectionState.Disconnected) return;
      joining = true; transition(lastRefresh ? "reconnecting" : "connecting");
      try {
        await connection.start();
        if (alive()) await room(true);
      } catch {
        if (alive()) {
          if (retryCount < retries.length) retryTimer = setTimeout(() => { void start(); }, retries[retryCount++]);
          else transition("disconnected");
        }
      } finally { joining = false; }
    }
    const refresh = () => { if (alive()) void room(needsJoin); };
    const handlers: Record<string, (...args: any[]) => void> = {
      Snapshot: (value: DiagramSyncCheckpoint) => { if (alive()) { setSnapshot(value); optionsRef.current.onCheckpoint?.(value); } },
      Update: (value: DiagramSyncCheckpoint) => {
        if (!alive()) return;
        setSnapshot(value); optionsRef.current.onCheckpoint?.(value); refresh();
      },
      Presence: () => { if (alive() && !needsJoin) refresh(); },
      Invalidate: (value: { diagram_id: string; reason: string }) => {
        // "archived" goes to the whole room; "access_revoked" only to the connections of a user who lost all access.
        if (alive() && value?.diagram_id === diagramId && (value.reason === "archived" || value.reason === "access_revoked")) {
          retire(); optionsRef.current.onUnavailable?.();
        }
      },
      Cursor: (value: Omit<CollaboratorCursor, "seenAt">) => {
        if (!alive() || !value || !roster.has(value.connectionId) || !cursorPositionSchema.safeParse(value).success) return;
        setCursors(current => [...current.filter(cursor => cursor.connectionId !== value.connectionId), { ...value, seenAt: Date.now() }]);
      },
    };
    for (const [event, handler] of Object.entries(handlers)) connection.on(event, handler);
    connection.onreconnecting(() => {
      if (!alive()) return;
      epoch++; refreshFlight = null; needsJoin = true; pendingCursor = null; transition("reconnecting");
      setCursors([]);
    });
    connection.onreconnected(() => { if (alive()) void room(true); });
    connection.onclose(() => { if (alive()) { epoch++; refreshFlight = null; needsJoin = true; pendingCursor = null; transition("disconnected"); } });
    const heartbeat = setInterval(refresh, 10_000);
    const expiry = setInterval(() => {
      if (!alive()) return;
      const now = Date.now();
      if (ready.current && now - lastRefresh > 15_000) transition("degraded");
      setCursors(current => current.filter(cursor => roster.has(cursor.connectionId) && now - cursor.seenAt < 5_000));
    }, 1_000);
    const cursorTimer = setInterval(() => {
      if (!alive() || !ready.current || !pendingCursor || cursorFlight || connection.state !== HubConnectionState.Connected) return;
      const sample = pendingCursor; pendingCursor = null; cursorFlight = true;
      void connection.invoke("SendCursor", diagramId, sample.x, sample.y)
        .catch(() => { if (alive()) transition("degraded"); })
        .finally(() => { cursorFlight = false; });
    }, 50);
    window.addEventListener("focus", refresh); window.addEventListener("online", refresh);
    controls.current = {
      retry: () => { retryCount = 0; void start(); }, refresh,
      degrade: () => { transition("degraded"); refresh(); }, retire,
      cursor: (x, y) => { if (alive() && ready.current && cursorPositionSchema.safeParse({ x, y }).success) pendingCursor = { x, y }; },
    };
    void start();
    return () => {
      active = false; epoch++; ready.current = false; pendingCursor = null;
      clearTimeout(retryTimer); clearInterval(heartbeat); clearInterval(expiry); clearInterval(cursorTimer);
      for (const timer of timeoutTimers) clearTimeout(timer);
      window.removeEventListener("focus", refresh); window.removeEventListener("online", refresh);
      for (const [event, handler] of Object.entries(handlers)) connection.off(event, handler);
      if (connectionRef.current === connection) connectionRef.current = null;
      void connection.stop();
    };
  }, [diagramId]);

  const sendUpdate = useCallback(async (payload: DiagramUpdate): Promise<DiagramSaveResult | DiagramConflict> => {
    const connection = connectionRef.current;
    if (!ready.current || connection?.state !== HubConnectionState.Connected || !diagramId) throw new Error("Diagram connection is offline");
    return connection.invoke("SendUpdate", diagramId, JSON.stringify(payload));
  }, [diagramId]);
  const sendCursor = useCallback((x: number, y: number) => controls.current.cursor(x, y), []);
  const retry = useCallback(() => controls.current.retry(), []), refresh = useCallback(() => controls.current.refresh(), []);
  const degrade = useCallback(() => controls.current.degrade(), []), retire = useCallback(() => controls.current.retire(), []);
  return { snapshot, presence, cursors, connectionId, status, isConnected: status === "connected", retry, refresh, degrade, retire, sendUpdate, sendCursor };
}
